import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  blogService,
  BlogPost,
  BlogDraft,
  saveDraftToLocalStorage,
  getDraftFromLocalStorage,
  clearDraftFromLocalStorage,
  normalizeImagePath
} from '../../services/blogService';

// Lazy load MDEditor to prevent SSR/production issues
const MDEditor = lazy(() => import('@uiw/react-md-editor'));

const PostEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNewPost = !id || id === 'new';

  // Form state
  const [postType, setPostType] = useState<'story' | 'news'>('story');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [featured, setFeatured] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [featuredImage, setFeaturedImage] = useState<BlogPost['featuredImage'] | null>(null);
  const [imageAlt, setImageAlt] = useState('');
  const [imageCaption, setImageCaption] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');

  // UI state
  const [loading, setLoading] = useState(!isNewPost);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Refs for tracking initial state
  const initialStateRef = useRef<string>('');
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Get current form state as string for comparison
  const getCurrentStateString = useCallback(() => {
    return JSON.stringify({ postType, title, content, featured, externalUrl, sourceName });
  }, [postType, title, content, featured, externalUrl, sourceName]);

  // Check if form has unsaved changes
  const checkDirty = useCallback(() => {
    const currentState = getCurrentStateString();
    return currentState !== initialStateRef.current;
  }, [getCurrentStateString]);

  // Auto-save to localStorage (debounced)
  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    if (title || content) {
      autoSaveTimerRef.current = setTimeout(() => {
        const draft: BlogDraft = {
          postId: id || 'new',
          timestamp: Date.now(),
          data: { postType, title, content, featured, externalUrl, sourceName }
        };
        saveDraftToLocalStorage(draft);
      }, 2000);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [postType, title, content, featured, externalUrl, sourceName, id]);

  // Update dirty state
  useEffect(() => {
    setIsDirty(checkDirty());
  }, [checkDirty]);

  // Browser beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Note: useBlocker removed due to compatibility issues
  // Using beforeunload event for unsaved changes warning instead

  // Load existing post or restore draft
  useEffect(() => {
    const loadPost = async () => {
      if (isNewPost) {
        // Check for existing draft
        const draft = getDraftFromLocalStorage();
        if (draft && draft.postId === 'new') {
          setPostType(draft.data.postType);
          setTitle(draft.data.title);
          setContent(draft.data.content);
          setFeatured(draft.data.featured);
          if (draft.data.externalUrl) setExternalUrl(draft.data.externalUrl);
          if (draft.data.sourceName) setSourceName(draft.data.sourceName);
        }
        initialStateRef.current = getCurrentStateString();
        return;
      }

      try {
        setLoading(true);
        const post = await blogService.fetchPostById(id!);
        setPostType(post.postType);
        setTitle(post.title);
        setContent(post.content);
        setFeatured(post.featured);
        setExternalUrl(post.externalUrl || '');
        setSourceName(post.sourceName || '');
        setFeaturedImage(post.featuredImage || null);
        setImageAlt(post.featuredImage?.alt || '');
        setImageCaption(post.featuredImage?.caption || '');
        setStatus(post.status);

        // Check for draft
        const draft = getDraftFromLocalStorage();
        if (draft && draft.postId === id) {
          const draftTime = new Date(draft.timestamp).toLocaleString();
          if (window.confirm(`You have unsaved changes from ${draftTime}. Restore them?`)) {
            setPostType(draft.data.postType);
            setTitle(draft.data.title);
            setContent(draft.data.content);
            setFeatured(draft.data.featured);
            if (draft.data.externalUrl) setExternalUrl(draft.data.externalUrl);
            if (draft.data.sourceName) setSourceName(draft.data.sourceName);
          } else {
            clearDraftFromLocalStorage();
          }
        }
      } catch (err) {
        console.error('Error loading post:', err);
        setError(err instanceof Error ? err.message : 'Failed to load post');
      } finally {
        setLoading(false);
      }
    };

    loadPost().then(() => {
      // Set initial state after loading
      setTimeout(() => {
        initialStateRef.current = getCurrentStateString();
      }, 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on id/isNewPost change, not form state
  }, [id, isNewPost]);

  const handleSave = async (publish = false) => {
    // Validate
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!content.trim()) {
      setError('Content is required');
      return;
    }
    if (postType === 'news') {
      if (!externalUrl.trim()) {
        setError('External URL is required for news posts');
        return;
      }
      if (!sourceName.trim()) {
        setError('Source name is required for news posts');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);

      const postData = {
        title,
        content,
        postType,
        featured,
        externalUrl: postType === 'news' ? externalUrl : undefined,
        sourceName: postType === 'news' ? sourceName : undefined,
        // Include featuredImage for new posts (already uploaded)
        featuredImage: isNewPost && featuredImage ? {
          ...featuredImage,
          alt: imageAlt,
          caption: imageCaption
        } : undefined
      };

      let savedPost: BlogPost;
      if (isNewPost) {
        savedPost = await blogService.createPost(postData);
      } else {
        savedPost = await blogService.updatePost(id!, postData);
      }

      if (publish && savedPost.status !== 'published') {
        savedPost = await blogService.publishPost(savedPost._id);
      }

      // Clear draft and update initial state
      clearDraftFromLocalStorage();
      initialStateRef.current = getCurrentStateString();
      setIsDirty(false);

      if (publish) {
        navigate('/admin/posts');
      } else if (isNewPost) {
        navigate(`/admin/posts/${savedPost._id}/edit`, { replace: true });
      }
    } catch (err) {
      console.error('Error saving post:', err);
      setError(err instanceof Error ? err.message : 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSaving(true);
      if (isNewPost) {
        // For new posts, just upload the image and store the result
        const uploadResult = await blogService.uploadImage(file);
        setFeaturedImage({
          filename: uploadResult.filename,
          originalName: uploadResult.originalName,
          path: uploadResult.path,
          alt: imageAlt,
          caption: imageCaption
        });
      } else {
        // For existing posts, update via the post endpoint
        const updatedPost = await blogService.updateFeaturedImage(id!, file, imageAlt, imageCaption);
        setFeaturedImage(updatedPost.featuredImage || null);
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!featuredImage) return;
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      setSaving(true);
      if (!isNewPost && id) {
        // For existing posts, delete via API
        await blogService.deleteFeaturedImage(id);
      }
      // For both new and existing, clear local state
      setFeaturedImage(null);
      setImageAlt('');
      setImageCaption('');
    } catch (err) {
      console.error('Error deleting image:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete image');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowUnsavedModal(true);
      setPendingNavigation(() => () => navigate('/admin/posts'));
    } else {
      navigate('/admin/posts');
    }
  };

  const handleDiscardChanges = () => {
    clearDraftFromLocalStorage();
    setIsDirty(false);
    setShowUnsavedModal(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{isNewPost ? 'New Post' : 'Edit Post'}</h1>
        {status === 'published' && (
          <span className="badge bg-success">Published</span>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      <form onSubmit={(e) => e.preventDefault()}>
        {/* Post Type Selection */}
        <div className="mb-4">
          <label className="form-label fw-bold d-block mb-2" style={{ fontSize: '1rem' }}>Post Type</label>
          <div className="btn-group" role="group">
            <input
              type="radio"
              className="btn-check"
              name="postType"
              id="type-story"
              checked={postType === 'story'}
              onChange={() => setPostType('story')}
            />
            <label className="btn btn-outline-primary rounded-start" htmlFor="type-story">
              <i className="fas fa-book-open me-2"></i>Story
            </label>
            <input
              type="radio"
              className="btn-check"
              name="postType"
              id="type-news"
              checked={postType === 'news'}
              onChange={() => setPostType('news')}
            />
            <label className="btn btn-outline-primary rounded-end" htmlFor="type-news">
              <i className="fas fa-newspaper me-2"></i>News
            </label>
          </div>
        </div>

        {/* Title */}
        <div className="mb-3">
          <label htmlFor="title" className="form-label fw-bold">
            Title <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Enter post title..."
          />
          <small className="text-muted">{title.length}/200 characters</small>
        </div>

        {/* News-specific fields */}
        {postType === 'news' && (
          <>
            <div className="mb-3">
              <label htmlFor="externalUrl" className="form-label fw-bold">
                External URL <span className="text-danger">*</span>
              </label>
              <input
                type="url"
                className="form-control"
                id="externalUrl"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="https://example.com/article"
              />
              <small className="text-muted">Link to the original news article</small>
            </div>
            <div className="mb-3">
              <label htmlFor="sourceName" className="form-label fw-bold">
                Source Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="sourceName"
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
                placeholder="e.g., CNN, Local News"
              />
            </div>
          </>
        )}

        {/* Content Editor */}
        <div className="mb-4" data-color-mode="light">
          <label className="form-label fw-bold">
            Content <span className="text-danger">*</span>
          </label>
          <Suspense fallback={
            <div className="border rounded p-4 text-center text-muted" style={{ height: 400 }}>
              <div className="spinner-border spinner-border-sm me-2"></div>
              Loading editor...
            </div>
          }>
            <MDEditor
              value={content}
              onChange={(val) => setContent(val || '')}
              height={400}
              preview="live"
            />
          </Suspense>
        </div>

        {/* Featured Image */}
        <div className="mb-4">
          <label className="form-label fw-bold">Featured Image</label>
          {featuredImage ? (
            <div className="card">
              <img
                src={normalizeImagePath(featuredImage.path)}
                alt={featuredImage.alt || 'Featured image'}
                className="card-img-top"
                style={{ maxHeight: '300px', objectFit: 'cover' }}
              />
              <div className="card-body">
                <div className="row g-2 mb-2">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Alt text"
                      value={imageAlt}
                      onChange={(e) => setImageAlt(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Caption"
                      value={imageCaption}
                      onChange={(e) => setImageCaption(e.target.value)}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleDeleteImage}
                  disabled={saving}
                >
                  <i className="fas fa-trash me-1"></i>Remove Image
                </button>
              </div>
            </div>
          ) : (
            <div>
              <input
                type="file"
                className="form-control"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                disabled={saving}
              />
              <small className="text-muted">JPEG, PNG, or WebP. Max 5MB.</small>
            </div>
          )}
        </div>

        {/* Featured Checkbox */}
        <div className="mb-4">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="featured"
              checked={featured}
              onChange={(e) => setFeatured(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="featured">
              <i className="fas fa-star text-warning me-1"></i>
              Feature this post on homepage
            </label>
          </div>
          <small className="text-muted">Featured posts appear prominently on the homepage.</small>
        </div>

        {/* Action Buttons */}
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              'Save Draft'
            )}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Publishing...
              </>
            ) : status === 'published' ? (
              'Update'
            ) : (
              'Publish'
            )}
          </button>
        </div>
      </form>

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Unsaved Changes</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowUnsavedModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>You have unsaved changes. Are you sure you want to leave?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUnsavedModal(false)}
                >
                  Keep Editing
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDiscardChanges}
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostEditorPage;
